import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { GET_FULL_EXAM_API, FULL_EXAM_API_UPDATE } from "../../services/apiConstants";
import ResultsModal from "./ResultsModal";
import "./fullExam.css";

const EXAM_DURATION = 30 * 60;

const FullExamDisplay = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [examTitle, setExamTitle] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [summary, setSummary] = useState({ correct: 0, incorrect: 0, notAnswered: 0 });
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const examData =
    location.state ||
    JSON.parse(localStorage.getItem("paymentData") || "null") ||
    JSON.parse(localStorage.getItem("examData") || "null");
  const { mockExamId, mockExamDesc } = examData || {};
  const paidExamIds = JSON.parse(localStorage.getItem("paidExamIds") || "[]");
  const isPaymentComplete = examData?.paid === true || paidExamIds.includes(mockExamId);

  useEffect(() => {
    if (!isPaymentComplete) {
      const paymentData = location.state || JSON.parse(localStorage.getItem("paymentData") || "null");
      if (paymentData?.mockExamId) {
        navigate("/UPIPayment", { state: paymentData, replace: true });
        return;
      }
      navigate("/Dashboard", { replace: true });
      return;
    }

    resetExamState();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetExamState = () => {
    setAnswers({});
    setMarked({});
    setSubmitted(false);
    setScore(0);
    setCurrentIndex(0);
    setTimeLeft(EXAM_DURATION);
    setSummary({ correct: 0, incorrect: 0, notAnswered: 0 });
    setShowResults(false);
  };

  const fetchQuestions = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    setLoadError("");

    try {
      const res = await axios.post(
        GET_FULL_EXAM_API,
        { mockExamId },
        { headers: { Authorization: `${token}`, "Content-Type": "application/json" } }
      );

      const fetchedQuestions = (res.data.questions || res.data).map((q) => {
        const correct = q.correctAnswer || q.answer || q.correctOption || "";
        return {
          ...q,
          id: q.id || q._id,
          correctAnswer: correct.toString().trim().toUpperCase(),
        };
      });

      setQuestions(fetchedQuestions);
      setExamTitle(res.data.examTitle || mockExamDesc);
      resetExamState();
    } catch (err) {
      console.error("Failed to fetch questions:", err);
      setLoadError("Failed to load exam. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (submitted || showResults || timeLeft <= 0) return undefined;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, submitted, showResults]);

  useEffect(() => {
    if (timeLeft === 0 && !submitted && !showResults) submitExam(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const handleAnswer = (qid, option) => {
    setAnswers((prev) => ({ ...prev, [qid]: option }));
  };

  const clearAnswer = (qid) => {
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[qid];
      return updated;
    });
  };

  const toggleMark = (qid) => {
    setMarked((prev) => ({ ...prev, [qid]: !prev[qid] }));
  };

  const submitExam = async (forceSubmit = false) => {
    if (submitted) return;

    const unanswered = questions.length - Object.keys(answers).length;
    if (!forceSubmit && unanswered > 0) {
      const shouldSubmit = window.confirm(
        `You still have ${unanswered} unanswered question${unanswered === 1 ? "" : "s"}. Submit exam now?`
      );
      if (!shouldSubmit) return;
    }

    let correct = 0;
    let incorrect = 0;
    let notAnswered = 0;

    questions.forEach((q) => {
      const userAnswer = (answers[q.id] || "").trim().toUpperCase();
      const correctAnswer = q.correctAnswer || "";

      if (!userAnswer) notAnswered++;
      else if (userAnswer === correctAnswer) correct++;
      else incorrect++;
    });

    setScore(correct);
    setSummary({ correct, incorrect, notAnswered });
    setSubmitted(true);
    setShowResults(true);

    const token = localStorage.getItem("token");
    try {
      await axios.post(
        FULL_EXAM_API_UPDATE,
        {
          mockExamId,
          score: correct,
          totalQuestions: questions.length,
          status: "COMPLETED",
        },
        { headers: { "Content-Type": "application/json", Authorization: `${token}` } }
      );
    } catch (err) {
      console.error("Submit failed:", err);
      alert("Failed to submit exam. Please try again.");
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="exam-state">
        <div className="exam-loader" />
        <h3>Loading your exam...</h3>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="exam-state">
        <h3>{loadError}</h3>
        <button onClick={fetchQuestions}>Try Again</button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="exam-state">
        <h3>No questions available for this exam.</h3>
        <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const markedCount = Object.values(marked).filter(Boolean).length;
  const unansweredCount = questions.length - answeredCount;

  const getStatusClass = (q, index) => {
    if (index === currentIndex) return "current";
    if (marked[q.id]) return "marked";
    if (answers[q.id]) return "answered";
    return "not-answered";
  };

  return (
    <div className="exam-layout exam-layout-single">
      <div className="exam-main">
        <div className="exam-header">
          <div>
            <p className="exam-eyebrow">Full Exam</p>
            <h2>{examTitle}</h2>
          </div>
          {!submitted && !showResults && (
            <div className={`timer ${timeLeft <= 60 ? "danger" : ""}`}>
              {formatTime(timeLeft)}
            </div>
          )}
        </div>

        {!showResults ? (
          <>
            <div className="question-progress-panel">
              <div className="question-progress-header">
                <h3>Question Progress</h3>
                <div className="question-progress-summary">
                  <span>{answeredCount} answered</span>
                  <span>{unansweredCount} unanswered</span>
                  <span>{markedCount} marked</span>
                </div>
                <div className="progress-legend">
                  <span><i className="legend-dot answered"></i>Answered</span>
                  <span><i className="legend-dot marked"></i>Marked for Review</span>
                  <span><i className="legend-dot not-answered"></i>Not Answered</span>
                  <span><i className="legend-dot current"></i>Current</span>
                </div>
              </div>

              <div className="question-progress-grid">
                {questions.map((q, index) => (
                  <button
                    key={q.id}
                    className={`progress-question ${getStatusClass(q, index)}`}
                    onClick={() => setCurrentIndex(index)}
                    aria-label={`Go to question ${index + 1}`}
                  >
                    <span>{index + 1}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="question-card">
              <div className="question-topline">
                <span>Question {currentIndex + 1} of {questions.length}</span>
                {marked[currentQ.id] && <strong>Marked for review</strong>}
              </div>
              <h3>{currentQ.questionText}</h3>

              {["A", "B", "C", "D"].map((opt) => (
                <label
                  key={opt}
                  className={`option ${answers[currentQ.id] === opt ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQ.id}`}
                    checked={answers[currentQ.id] === opt}
                    onChange={() => handleAnswer(currentQ.id, opt)}
                  />
                  <span className="option-key">{opt}</span>
                  <span>{currentQ[`option${opt}`]}</span>
                </label>
              ))}

              <div className="nav-buttons">
                <button
                  onClick={() => setCurrentIndex((i) => i - 1)}
                  disabled={currentIndex === 0}
                >
                  Previous
                </button>

                <button
                  className="secondary-action"
                  onClick={() => clearAnswer(currentQ.id)}
                  disabled={!answers[currentQ.id]}
                >
                  Clear Answer
                </button>

                <button className="review-action" onClick={() => toggleMark(currentQ.id)}>
                  {marked[currentQ.id] ? "Unmark Review" : "Mark for Review"}
                </button>

                <button
                  onClick={() => setCurrentIndex((i) => i + 1)}
                  disabled={currentIndex === questions.length - 1}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="submit-row">
              <span>Review your marked questions before submitting.</span>
              <button className="submit-btn" onClick={() => submitExam(false)}>
                Submit Exam
              </button>
            </div>
          </>
        ) : null}

        {showResults && (
          <ResultsModal
            questions={questions}
            answers={answers}
            summary={summary}
            score={score}
            onClose={() => navigate("/dashboard")}
          />
        )}
      </div>

    </div>
  );
};

export default FullExamDisplay;
